import * as THREE from "three";
import { log, error, warn, info, debug } from '../../../utils/consoleLogger';

export const CreateCoordinates = (size, isCalculated, { ObjectDimensions }, numDigits = 0, units = "cm") => {
  // Convert the positions into a THREE.Vector3 acculmulator object

  if (size === undefined) return;

  log('size, object dimensions:', size, ObjectDimensions);
  log('num digits:', numDigits);

  const getScaleFactor = (units) => {
    switch (units) {
      case "mm":
        return 1000; // mm to m
      case "cm":
        return 100; // cm to m
      default:
        return 1; // m to m
    }
  };

  const scaleFactor = isCalculated ? getScaleFactor(units) : getScaleFactor(ObjectDimensions.modelSetup.units);
  log('scale factor:', scaleFactor);

  // Helper function to parse "x, y, z" into THREE.Vector3
  const parseVector = (str, offset, offsetDirection) => {
    let [x, y, z] = str.split(",").map(Number);
    if (offsetDirection) {
      const offsetValue = offset;
      if (offsetDirection === "x") {
        x += offsetValue;
      } else if (offsetDirection === "y") {
        y += offsetValue;
      } else if (offsetDirection === "z") {
        z += offsetValue;
      }
    }
    // TODO: Fix the impact of scaleFactor on label positioning
    if (!isCalculated) return new THREE.Vector3(x / scaleFactor, y / scaleFactor, z / scaleFactor);
    return new THREE.Vector3(x, y, z);
  };

  // Different as the offset value isn't added, it replaces allowing for diameters shown on the same line
  const parseDiameterVector = (str, offset, offsetDirection) => {
    let [x, y, z] = str.split(",").map(Number);
    if (offsetDirection) {
      const offsetValue = offset;
      if (offsetDirection === "x") {
        x = offsetValue;
      } else if (offsetDirection === "y") {
        y = offsetValue;
      } else if (offsetDirection === "z") {
        z = offsetValue;
      }
    }
    // TODO: Fix the impact of scaleFactor on label positioning
    if (!isCalculated) return new THREE.Vector3(x / scaleFactor, y / scaleFactor, z / scaleFactor);
    return new THREE.Vector3(x, y, z);
  };

  // Process arrowPositioning into a Vector3 format
  // TODO: if statement for when positions are not included
  const positions = ObjectDimensions.arrowPositioning.map((arrow) => {
    const position = {
      // Spreads the arrow object to include all other properties except start and end
      ...arrow,
      start: parseVector(arrow.start, arrow.offset, arrow.offsetDirection),
      end: parseVector(arrow.end, arrow.offset, arrow.offsetDirection),
    };
    // Return values before offset if offset is applied
    if (arrow.offset !== undefined) {
      position.offsetstart = parseVector(arrow.start);
      position.offsetend = parseVector(arrow.end);
    }
    return position;
  });

  // Process diameters into a Vector3 format
  const diameters = ObjectDimensions.diameter
  ? ObjectDimensions.diameter.map((value) => {
    log("diameter:", ObjectDimensions.diameter);
    if (!value.offset) {
      value.offset = 10; // Default offset value
    }

    // In order to add the slight angle to the diameter lines
    const DiameterLineAdjustment = new THREE.Vector3(
      0,
      (-0.15 * value.offset) / scaleFactor,
      0
    );
    const diameterObj = {
      ...value,
      start: parseDiameterVector(value.start),
    };
    if (value.offset !== undefined) {
      diameterObj.end = parseDiameterVector(
        value.start,
        value.offset,
        value.offsetDirection
      ).add(DiameterLineAdjustment);
    }
    return diameterObj;
  }) : []; // If diameter is undefined, return an empty array

  log("Harry Diameters: ", diameters);

  // Function to apply coordinate transformations
  const transformPositions = (posArray, transformFn) =>
    posArray.map((arrow) => {
      if (arrow.fixed || arrow.isRotational) {
        return arrow;
      } else {
        const transformed = {
          ...arrow,
          start: transformFn(arrow.start),
          end: transformFn(arrow.end),
        };
        if (arrow.offsetstart !== undefined && arrow.offsetend !== undefined) {
          transformed.offsetstart = transformFn(arrow.offsetstart);
          transformed.offsetend = transformFn(arrow.offsetend);
        }
        return transformed;
      }
    });

//How can we both handle the case when the coordinates start in the bottom left corner and when they start in the middle of the screw?

  // Transformation functions
  const invertX = (vec) => new THREE.Vector3(vec.x >= 0 ? (size.x - vec.x) * (isCalculated ? -1 : 1) : size.x + vec.x, vec.y, vec.z);
  const invertZ = (vec) => new THREE.Vector3(vec.x, vec.y, vec.z >= 0 ? (size.z - vec.z) * (isCalculated ? -1 : 1) : size.z + vec.z);
  const invertXZ = (vec) => new THREE.Vector3(
      vec.x >= 0 ? (size.x - vec.x) * (isCalculated ? -1 : 1) : size.x + vec.x, 
      vec.y, 
      vec.z >= 0 ? (size.z - vec.z) * (isCalculated ? -1 : 1) : size.z + vec.z);

  // Generate transformed positions
  const positionsXInverted = transformPositions(positions, invertX);
  const positionsZInverted = transformPositions(positions, invertZ);
  const positionsXInvertedZInverted = transformPositions(positions, invertXZ);

  log("Harrypositions: ", positions);
  log("HarrypositionsXInverted: ", positionsXInverted);
  log("HarrypositionsZInverted: ", positionsZInverted);
  log(
    "HarrypositionsXInvertedZInverted: ",
    positionsXInvertedZInverted
  );

  // Instead refer them relative to a different origin at the middle bottom of the object used by three.js
  const TransformVecOrigin = (vec) => {
    const transformedX = vec.x - size.x / 2;
    const transformedY = vec.y;
    const transformedZ = size.z / 2 - vec.z;
    return new THREE.Vector3(transformedX, transformedY, transformedZ);
  };

  const convertPositions = (posArray) => {
    if (
      ObjectDimensions.modelSetup.origin &&
      ObjectDimensions.modelSetup.origin === "center"
    ) {
      return posArray;
    } else {
      return posArray.map((arrow) => {
        const transformed = {
          ...arrow,
          start: TransformVecOrigin(
            new THREE.Vector3(arrow.start.x, arrow.start.y, arrow.start.z)
          ),
          end: TransformVecOrigin(
            new THREE.Vector3(arrow.end.x, arrow.end.y, arrow.end.z)
          ),
        };
        if (arrow.offsetstart !== undefined && arrow.offsetend !== undefined) {
          transformed.offsetstart = TransformVecOrigin(
            new THREE.Vector3(
              arrow.offsetstart.x,
              arrow.offsetstart.y,
              arrow.offsetstart.z
            )
          );
          transformed.offsetend = TransformVecOrigin(
            new THREE.Vector3(
              arrow.offsetend.x,
              arrow.offsetend.y,
              arrow.offsetend.z
            )
          );
        }
        return transformed;
      });
    }
  };

  const positionsConverted = convertPositions(positions);
  const positionsXInvertedConverted = convertPositions(positionsXInverted);
  log('positionsXinverted, converted:', positionsXInverted, positionsXInvertedConverted);
  const positionsZInvertedConverted = convertPositions(positionsZInverted);
  const positionsXInvertedZInvertedConverted = convertPositions(
    positionsXInvertedZInverted
  );

  // Function to apply coordinate transformations for diameters depending on origin
  const convertDiameters = (diameterArray) => {
    diameterArray.forEach((diameter) => {
      diameter.value = `⌀ ${diameter.value}${units}`;
    });
    if (
      ObjectDimensions.modelSetup.origin &&
      ObjectDimensions.modelSetup.origin === "center"
    ) {
      return diameterArray;
    } else {
      return diameterArray.map((diameter) => {
        const transformed = {
          ...diameter,
          start: TransformVecOrigin(
            new THREE.Vector3(
              diameter.point.x,
              diameter.point.y,
              diameter.point.z
            )
          ),
          end: TransformVecOrigin(
            new THREE.Vector3(
              diameter.point.x,
              diameter.point.y,
              diameter.point.z
            )
          ),
        };
        return transformed;
      });
    }
  };

  const diametersConverted = convertDiameters(diameters);
  log("Harry Diameters Converted: ", diametersConverted);

  const getLabelsFromDiameters = (diameters) => {
    //Add an offset on the y axis to make the labels appear below the diameter line
    const offset = 1 / scaleFactor;
    return diameters.map((diameter) => {
      const label = {
        ...diameter,
        start: new THREE.Vector3(
          diameter.start.x,
          diameter.start.y - offset,
          diameter.start.z
        ),
        end: new THREE.Vector3(
          diameter.end.x,
          diameter.end.y - offset,
          diameter.end.z
        ),
      };
      return label;
    });
  }

  //Add diametersLabels to handle labels for diameters differently
  const diameterLabels = getLabelsFromDiameters(diametersConverted);

  // Function to create position labels at midpoints with offset
  
  const createPositionLabels = (posArray) =>
    posArray.map(({ start, end }) => {
      const midpoint = new THREE.Vector3(
        (start.x + end.x) / 2,
        (start.y + end.y) / 2,
        (start.z + end.z) / 2
      );
      
      // Calculate offset direction (perpendicular to line, away from origin)
      const lineDirection = new THREE.Vector3().subVectors(end, start).normalize();
      const offsetDistance = isCalculated ?(ObjectDimensions.modelSetup.labelOffset || 10) / scaleFactor : 3 / scaleFactor;

      // Determine offset direction based on line orientation - create new Vector3 instances
      let offsetDirection;
      if (isCalculated) {
        if (Math.abs(lineDirection.x) > 0.01) { // X-axis line
          offsetDirection = new THREE.Vector3(0, 0, midpoint.z >= 0 ? offsetDistance : -offsetDistance);
        } else if (Math.abs(lineDirection.y) > 0.01) { // Y-axis line
          offsetDirection = new THREE.Vector3(midpoint.x >= 0 ? offsetDistance / 1.5 : -offsetDistance / 1.5, 0,
            midpoint.z >= 0 ? offsetDistance / 1.5 : -offsetDistance / 1.5);
        } else { // Z-axis line
          offsetDirection = new THREE.Vector3(midpoint.x >= 0 ? offsetDistance : -offsetDistance, 0, 0);
        }
      }
      else {
        if (Math.abs(lineDirection.x) > 0.01) { // X-axis line
          offsetDirection = new THREE.Vector3(0, 0, midpoint.z >= 0 ? offsetDistance : -offsetDistance);
        } else if (Math.abs(lineDirection.y) > 0.01) { // Y-axis line
          offsetDirection = new THREE.Vector3(midpoint.x >= 0 ? offsetDistance / 1.5 : -offsetDistance / 1.5, 0,
            0);
        } else { // Z-axis line
          offsetDirection = new THREE.Vector3(midpoint.x >= 0 ? offsetDistance : -offsetDistance, 0, 0);
        }
      }
      
      
      
      const label = midpoint.clone().add(offsetDirection);
      const length = start.distanceTo(end);
      const convertedlength = Number(length) * scaleFactor;
      return {
        label,
        labelText: `${Number(convertedlength.toFixed(numDigits)).toString()}${
          units
        }`,
      };
    });

  // Runs the functions above to create the labels for each view option
  const positionLabels = createPositionLabels(positionsConverted);
  log('x inverted, z inverted, xz inverted:', positionsXInvertedConverted, positionsZInvertedConverted, positionsXInvertedZInvertedConverted);
  const positionLabelsXInverted = createPositionLabels(
    positionsXInvertedConverted
  );
  const positionLabelsZInverted = createPositionLabels(
    positionsZInvertedConverted
  );
  const positionLabelsXInvertedZInverted = createPositionLabels(
    positionsXInvertedZInvertedConverted
  );

  log("Harry Converted Positions:", positionsConverted);
  log("Harry Position Labels:", positionLabels);
  // log("Harry Position Labels X Inverted:", positionLabelsXInverted);
  // log("Harry Position Labels Z Inverted:", positionLabelsZInverted);
  // log(
  //   "Harry Position Labels X and Z Inverted:",
  //   positionLabelsXInvertedZInverted
  // );

  return {
    positions: positionsConverted,
    positionLabels,
    positionsXInverted: positionsXInvertedConverted,
    positionLabelsXInverted,
    positionsZInverted: positionsZInvertedConverted,
    positionLabelsZInverted,
    positionsXInvertedZInverted: positionsXInvertedZInvertedConverted,
    positionLabelsXInvertedZInverted,
    diameters: diametersConverted,
    diameterLabels: diameterLabels,
    scaleFactor,
  };
};